import { Account, Client, OAuthProvider } from 'appwrite';
import type { Models } from 'appwrite';
import { VIEWER_BASE_URL } from '@lved/shared-core';
import type { GitHubAuthConfig } from './githubTypes.js';

/**
 * Service for GitHub OAuth authentication using AppWrite
 *
 * This service handles the entire OAuth flow client-side.
 * No server functions are needed for authentication.
 *
 * Flow:
 * 1. User clicks "Sign in with GitHub"
 * 2. AppWrite redirects to GitHub for authorization
 * 3. GitHub redirects back to the application
 * 4. AppWrite handles token exchange automatically
 * 5. User is authenticated with GitHub token stored in Appwrite
 */
export class GitHubAuthService {
    private client: Client;
    private account: Account;

    constructor(config: GitHubAuthConfig) {
        this.client = new Client().setEndpoint(config.endpoint).setProject(config.projectId);
        this.account = new Account(this.client);
    }

    /**
     * Authenticate user with GitHub OAuth
     *
     * This method initiates the OAuth flow entirely client-side.
     * AppWrite handles the redirect to GitHub and back automatically.
     * No server function is needed for this process.
     */
    async authenticateWithGitHub(): Promise<boolean> {
        try {
            const successUrl: string = GitHubAuthService.getSuccessRedirectUrl();
            const failureUrl: string = GitHubAuthService.getFailureRedirectUrl();
            // AppWrite will handle the redirect automatically based on your OAuth provider settings
            this.account.createOAuth2Session(OAuthProvider.Github, successUrl, failureUrl, ['user:email', 'repo']);
            console.debug('Redirecting to GitHub for authentication...');
            return true;
        } catch (error: unknown) {
            console.error(`GitHub authentication failed: ${String(error)}`);
            return false;
        }
    }

    /**
     * Check if user is authenticated with GitHub.
     * This checks if the user has a GitHub token stored in their AppWrite user preferences.
     */
    async isGitHubAuthenticated(): Promise<boolean> {
        try {
            const user: Models.User = await this.account.get();
            return Boolean(user.status);
        } catch {
            return false;
        }
    }

    /**
     * Get current authenticated user
     */
    async getCurrentUser(): Promise<Models.User | null> {
        try {
            return await this.account.get();
        } catch (error: unknown) {
            console.error(`Error getting current user: ${String(error)}`);
            return null;
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        try {
            await this.account.deleteSessions();
        } catch (error: unknown) {
            console.error(`Error logging out: ${String(error)}`);
        }
    }

    /**
     * Get the AppWrite client instance with the authenticated user's GitHub token (if available).
     * This is used by other services (like AppwriteGitHubService) to make authenticated API calls through Appwrite Functions.
     */
    getClient(): Client {
        return this.client;
    }

    private static getSuccessRedirectUrl(): string {
        const redirectUrl: URL = GitHubAuthService.getRedirectBaseUrl();
        if (typeof window !== 'undefined') {
            redirectUrl.search = window.location.search;
        }
        return redirectUrl.toString();
    }

    private static getFailureRedirectUrl(): string {
        return new URL('fail', GitHubAuthService.getRedirectBaseUrl()).toString();
    }

    private static getRedirectBaseUrl(): URL {
        const redirectUrl: URL =
            typeof window === 'undefined' ? new URL(VIEWER_BASE_URL) : new URL(window.location.href);
        redirectUrl.search = '';
        redirectUrl.hash = '';
        return redirectUrl;
    }
}
